import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FormHeader } from "@/components/FormHeader";
import { FormCover } from "@/components/FormCover";
import { FormattedCopyMarkdown } from "@/components/form-copy/FormattedCopyMarkdown";
import { cn } from "@/lib/utils";
import type { FormTheme } from "@kushya/shared";

export interface CompletionScreenProps {
  alignment: "left" | "center" | "right";
  logoUrl?: string;
  formName?: string;
  theme?: FormTheme;
  thankYouCopy?: string | null;
  responseId: string | null;
  isSubmitted: boolean;
  onReset: () => void;
}

export function CompletionScreen({
  alignment,
  logoUrl,
  formName,
  theme,
  thankYouCopy,
  responseId,
  isSubmitted,
  onReset,
}: CompletionScreenProps) {
  const completionContent = thankYouCopy || "Thank you for your submission!";
  const contentAlignmentClass =
    alignment === "center"
      ? "text-center"
      : alignment === "right"
        ? "text-right"
        : "text-left";
  const iconAlignmentClass =
    alignment === "center"
      ? "justify-center"
      : alignment === "right"
        ? "justify-end"
        : "justify-start";

  return (
    <div className="form-shell">
      <div className="mx-auto max-w-2xl">
        <FormHeader
          logoUrl={logoUrl}
          formName={formName}
          alignment={alignment}
        />
        <Card className={cn("form-paper p-8 md:p-12", contentAlignmentClass)}>
          <FormCover theme={theme} alignment={alignment} />
          <div className="form-step">
            <div className={cn("mb-6 flex", iconAlignmentClass)}>
              <svg
                className="size-16 text-green-500"
                aria-hidden="true"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <FormattedCopyMarkdown
              content={completionContent}
              variant="completion"
              className="form-heading mb-3"
            />

            <p className="text-base text-muted-foreground">
              {isSubmitted
                ? "Your response has been recorded successfully."
                : "Your responses have been recorded successfully."}
            </p>

            {responseId ? (
              <p className="mt-4 text-sm text-muted-foreground">
                Reference ID:{" "}
                <code className="rounded bg-muted px-2 py-1 text-xs font-medium text-foreground">
                  {responseId}
                </code>
              </p>
            ) : null}

            <Button variant="link" className="mt-8" onClick={onReset}>
              Submit another response
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
